import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import JjfLogo from "../../images/jjf-india-new-logo.png";
import "../../main-component/HomePage2/homeV2.css";

/* ------------------------------------------------------------------ */
/*  Shared navbar for the new "jjfv2" design system. Mirrors the header */
/*  built into the home page (MainPage.js) so every page shares one     */
/*  consistent, glassy navigation. Must be rendered inside a `.jjfv2`    */
/*  container for the scoped styles to apply.                            */
/* ------------------------------------------------------------------ */

const HEART_PATH =
  "M12 21s-7-4.6-9.3-9C1.2 9 2.6 5.5 6 5.2c2-.2 3.4 1 4.4 2.4C11.4 9 12 10 12 10s.6-1 1.6-2.4C14.6 6.2 16 5 18 5.2c3.4.3 4.8 3.8 3.3 6.8C19 16.4 12 21 12 21Z";

const MENU = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    children: [
      { label: "Who We Are?", to: "/about-us/who-we-are" },
      { label: "Our Journey", to: "/about-us/our-journey" },
      { label: "Leadership Team", to: "/about-us/leadership-team" },
    ],
  },
  {
    label: "Our Initiative",
    // Only Life Saver Army is live in the new design for now; the other
    // initiative pages still run the legacy header, so they stay out of the nav.
    children: [
      { label: "Life Saver Army", to: "/initiatives/life-saver-army" },
    ],
  },
  {
    label: "Impact & Stories",
    children: [
      { label: "Success Stories", to: "/impact/success-stories" },
      { label: "Testimonials", to: "/impact/testimonials" },
      { label: "Achievements", to: "/impact/achievements" },
    ],
  },
  {
    label: "Gallery",
    children: [
      { label: "Blood Donation Camps", to: "/gallery/blood-donation-camps" },
      { label: "Awareness Campaigns", to: "/gallery/awareness-campaigns" },
    ],
  },
  {
    label: "Join Us",
    children: [
      { label: "Volunteer Opportunities", to: "/join-us/volunteer-opportunities" },
    ],
  },
  {
    label: "Our Bulletin",
    children: [
      { label: "Blogs", to: "/our-bulletin/blogs" },
      { label: "Newsletters", to: "/our-bulletin/newsletters" },
    ],
  },
];

const Heart = ({ cls }) => (
  <svg className={cls} viewBox="0 0 24 24" fill="none">
    <path d={HEART_PATH} fill="currentColor" />
  </svg>
);

const HeaderV2 = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);

  /* sticky header on scroll */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const next = y > 30;
      setScrolled((p) => (p === next ? p : next));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* lock body scroll while the mobile drawer is open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const scrollTop = () => window.scrollTo(10, 0);
  const toggleSub = (label) => setOpenSub((s) => (s === label ? null : label));
  const closeMobile = () => {
    setMobileOpen(false);
    setOpenSub(null);
  };

  const isActive = (item) => {
    if (item.to) {
      return item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
    }
    if (item.children) {
      return item.children.some((c) => pathname.startsWith(c.to));
    }
    return false;
  };

  return (
    <header className={"header" + (scrolled ? " scrolled" : "")} id="jjfv2-header">
      <div className="topbar">
        <div className="wrap topbar-inner">
          <span className="tb-left">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d={HEART_PATH} />
            </svg>
            Serving communities since 2017 <span className="sep">·</span> Seva · Community · Impact
          </span>
          <span className="tb-right">
            <span className="lbl">Follow us</span>
            <span className="tb-social">
              <a href="https://www.instagram.com/TeamJJFIndia/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.9" /><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.9" /><circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" /></svg>
              </a>
              <a href="https://www.linkedin.com/company/TeamJJFIndia/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" fill="none"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.2 9.2h3.6V21H3.2V9.2Zm6 0h3.45v1.6h.05c.48-.9 1.66-1.85 3.42-1.85 3.66 0 4.33 2.3 4.33 5.3V21h-3.6v-5.3c0-1.26-.02-2.9-1.8-2.9-1.8 0-2.08 1.38-2.08 2.8V21H9.2V9.2Z" fill="currentColor" /></svg>
              </a>
              <a href="https://www.facebook.com/TeamJJFIndia/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="none"><path d="M14 8h2V5h-2a3 3 0 0 0-3 3v2H9v3h2v6h3v-6h2l1-3h-3V8a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>
              </a>
              <a href="https://www.youtube.com/@TeamJJFIndia/" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="1.9" /><path d="M10 9l5 3-5 3V9Z" fill="currentColor" /></svg>
              </a>
            </span>
          </span>
        </div>
      </div>

      <div className="wrap">
        <nav className="nav" aria-label="Primary">
          <Link to="/" className="brand" onClick={scrollTop} aria-label="Jainam Jivika Foundation home">
            <img src={JjfLogo} className="logo-img" alt="Jainam Jivika Foundation" />
          </Link>

          <div className="nav-links">
            {MENU.map((item) =>
              item.children ? (
                <div className="nav-item" key={item.label}>
                  <span className={"nav-top" + (isActive(item) ? " active" : "")}>
                    {item.label}
                    <svg className="caret" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <div className="nav-dropdown">
                    {item.children.map((c) => (
                      <Link key={c.label} to={c.to} onClick={scrollTop}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="nav-item" key={item.label}>
                  <Link to={item.to} className={"nav-top" + (isActive(item) ? " active" : "")} onClick={scrollTop}>
                    {item.label}
                  </Link>
                </div>
              )
            )}
          </div>

          <div className="nav-cta">
            <a
              href="/pdfs/jainam-jivika-foundation-profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--ghost"
            >
              <svg viewBox="0 0 24 24" fill="none"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="7.5" r="3.3" stroke="currentColor" strokeWidth="1.8" /></svg>
              Profile
            </a>
            <Link to="/join-us/donate" onClick={scrollTop} className="btn btn--green donate" data-burst="1">
              Donate Now
              <Heart cls="hb" />
            </Link>
            <button className={"burger" + (mobileOpen ? " open" : "")} aria-label="Open menu" onClick={() => setMobileOpen((o) => !o)}>
              <span></span>
            </button>
          </div>
        </nav>

        {/* mobile menu */}
        <div className={"nav-mobile" + (mobileOpen ? " open" : "")}>
          {MENU.map((item) =>
            item.children ? (
              <div className={"m-item" + (openSub === item.label ? " open" : "")} key={item.label}>
                <button className="m-top" onClick={() => toggleSub(item.label)}>
                  {item.label}
                  <svg className="caret" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className="m-sub">
                  {item.children.map((c) => (
                    <Link key={c.label} to={c.to} onClick={closeMobile}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div className="m-item" key={item.label}>
                <Link className="m-top" to={item.to} onClick={closeMobile}>
                  {item.label}
                </Link>
              </div>
            )
          )}

          {/* CTAs that live in the top bar on desktop but are otherwise
              unreachable once the drawer takes over on mobile. */}
          <div className="m-cta">
            <a
              href="/pdfs/jainam-jivika-foundation-profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--ghost"
              onClick={closeMobile}
            >
              <svg viewBox="0 0 24 24" fill="none"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="7.5" r="3.3" stroke="currentColor" strokeWidth="1.8" /></svg>
              Profile
            </a>
            <Link to="/join-us/donate" onClick={closeMobile} className="btn btn--green" data-burst="1">
              Donate Now
              <Heart cls="hb" />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderV2;
